/**
 * AR-380 — the Account center's pages, in one place.
 *
 * Account is a Level 2 module (the same pattern as Finances): Level 1 collapses,
 * the Level 2 column lists every page, and every page has its own address.
 *
 * v15 (2026-09-18) reshaped this. It was thirteen pages in three groups behind
 * an overview; it is now NINE, flat, with no group headings and no overview —
 * what the overview carried is an action-required notice above every page
 * (D-ACCT-25, D-ACCT-28). The five Platform pages left for the Settings module,
 * which is AR-390's to build and explicitly not this ticket's.
 *
 * The nav column, the router and the settings-search index all read this file,
 * so a page cannot exist in one and be missing from another.
 */

/** Every page of the center, in column order. */
export const ACCOUNT_PAGE_IDS = [
  'personal',
  'gem',
  'business',
  'membership',
  'billing',
  'public',
  'documents',
  'team',
  'password',
] as const;

export type AccountPageId = (typeof ACCOUNT_PAGE_IDS)[number];

const BASE = '/account';

/**
 * Every flag the Account center needs, parent first — the sub-flag is
 * meaningless without it (the pattern `onboarding` already uses on the server).
 *
 * The routes, the Profile / Team Management redirects and the profile-photo
 * menu row ALL read this one constant. Kept apart they would drift, and the
 * failure is silent and nasty: a redirect that still fires while the routes are
 * gated off sends the member to a page that no longer exists.
 */
export const ACCOUNT_CENTER_FLAGS = [
  'proMigration',
  'proMigrationAccountCenter',
] as const;

/**
 * Canonical client route for each page. There is no overview page any more, so
 * `/account` itself is the module home and lands on User Account.
 */
export const ACCOUNT_PATHS: Record<AccountPageId, string> = {
  personal: `${BASE}/user`,
  gem: `${BASE}/gem`,
  business: `${BASE}/business`,
  membership: `${BASE}/plan`,
  billing: `${BASE}/billing`,
  public: `${BASE}/brand`,
  documents: `${BASE}/documents`,
  team: `${BASE}/team`,
  password: `${BASE}/password`,
};

/** `/account` is the module home; it resolves to the first page. */
export const ACCOUNT_HOME = BASE;
export const ACCOUNT_HOME_PAGE: AccountPageId = 'personal';

/**
 * Title and standfirst. Plan + Agreements takes a function because an associate
 * advisor's page carries no plan and no commission, and a page must not
 * describe rows it does not show (A3).
 */
export const ACCOUNT_PAGE_TITLES: Record<
  AccountPageId,
  readonly [string, string | ((isOwner: boolean) => string)]
> = {
  personal: [
    'User Account',
    'Your contact details, mailing address and billing address.',
  ],
  gem: ['GEM Status', 'Your GEM level, what you have sold this year and what comes next.'],
  business: ['Your Business', 'The legal and commercial identity of your business.'],
  membership: [
    'Plan + Agreements',
    (isOwner: boolean) =>
      isOwner
        ? 'Your plan, your commission terms, agreements and credentials.'
        : 'Your membership, agreements and credentials.',
  ],
  billing: ['Billing', 'Everything you subscribe to, your payment method and your receipts.'],
  public: [
    'Brand + Public Presence',
    'Information entered on this page is visible on your public Advisor Showcase listing.',
  ],
  documents: [
    'Documents',
    'Your business continuity designation, tax information and the documents you keep yourself.',
  ],
  team: [
    'Team + Access',
    'Who is on your team, what they can see, and the defaults they inherit.',
  ],
  password: ['Password', 'Your sign-in password.'],
};

/**
 * The pages only the account Owner sees. v15 moved this: it was Membership and
 * Billing, it is now Your Business and Billing (D-ACCT-39). WorldVia has no
 * agreement with an associate's legal entity, so the business page is not
 * theirs; Plan + Agreements is everyone's, reduced for an associate (D-ACCT-40).
 */
export const ACCOUNT_OWNER_ONLY: readonly AccountPageId[] = ['business', 'billing'];

/**
 * Team + Access is the agency owner's page. The roster and the roles land in
 * sprint 1; data access and team defaults come at sprint 2 (D-ACCT-33).
 */
export const ACCOUNT_OWNER_ONLY_TEAM = true;

/**
 * Pages the current sprint does not carry. Documents is absent from the column
 * at sprint 1 and shows from sprint 2 (D-ACCT-35) — and with it, the
 * action-required item that would otherwise point at a page nobody can open.
 */
export const ACCOUNT_SPRINT = 1;
export const ACCOUNT_PAGES_FROM_SPRINT: Partial<Record<AccountPageId, number>> = {
  documents: 2,
};

export const accountPageInSprint = (page: AccountPageId): boolean =>
  (ACCOUNT_PAGES_FROM_SPRINT[page] ?? 1) <= ACCOUNT_SPRINT;

/** Short label for a page in the Level 2 column. */
export const ACCOUNT_NAV_LABELS: Record<AccountPageId, string> = {
  personal: 'User Account',
  gem: 'GEM Status',
  business: 'Your Business',
  membership: 'Plan + Agreements',
  billing: 'Billing',
  public: 'Brand + Public Presence',
  documents: 'Documents',
  team: 'Team + Access',
  password: 'Password',
};

/**
 * Account Route `path` props in App.tsx — must stay synchronized with
 * ACCOUNT_PATHS. The module home is registered last so the deeper paths match
 * first.
 */
export const ACCOUNT_APP_ROUTE_PATHS = [
  ...ACCOUNT_PAGE_IDS.map((id) => ACCOUNT_PATHS[id]),
  ACCOUNT_HOME,
];

/** Paths that put the app inside the Account center. */
export const ACCOUNT_NAV_MATCH_PATHS = [BASE] as const;

export const isAccountPath = (location: string): boolean =>
  location === BASE || location.startsWith(`${BASE}/`);

/** The page id a location resolves to, or null when the location is outside the center. */
export function accountPageIdFor(location: string): AccountPageId | null {
  const hit = ACCOUNT_PAGE_IDS.find((id) => ACCOUNT_PATHS[id] === location);
  return hit ?? null;
}
